import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

/**
 * One step in the breadcrumb trail. `href` makes it a link; omit it for the
 * current page.
 */
export type Crumb = {
    label: string;
    href?: string;
};

type BreadcrumbsContextType = {
    /** Null means "no page has set a trail" — the bar falls back to the path. */
    trail: Crumb[] | null;
    setTrail: (trail: Crumb[] | null) => void;
};

const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(undefined);

/**
 * Replaces three hardcoded slots (`company_name`, `product_name`,
 * `route_name`) that only ever worked for the one page that wrote them.
 *
 * Two problems that shape drove:
 *
 *  - Adding a breadcrumb anywhere else meant adding a field to this context.
 *  - Nothing cleared them. Only the reports page wrote labels and only while it
 *    was mounted, so drilling into a company and then navigating to Products
 *    left "Baby Balance – Royalties" hanging off the Products crumb.
 *
 * A trail is now set by whichever page knows its own name, and cleared when
 * that page unmounts — so a stale label cannot outlive the screen that set it.
 */
export const BreadcrumbsProvider = ({children}: { children: ReactNode }) => {
    const [trail, setTrail] = useState<Crumb[] | null>(null);

    const value = useMemo(() => ({trail, setTrail}), [trail]);

    return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBreadcrumbsContext = () => {
    const context = useContext(BreadcrumbsContext);
    if (context === undefined) {
        throw new Error('useBreadcrumbsContext must be used within a BreadcrumbsProvider');
    }
    return context;
};

/**
 * Declare this page's breadcrumb trail. Cleared automatically on unmount.
 *
 * Pass `null` while the name is still loading — the bar falls back to the path
 * rather than flashing a half-built trail.
 *
 * The array may be rebuilt on every render; only its CONTENT is compared, so
 * callers do not have to memoise it.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBreadcrumb = (crumbs: Crumb[] | null) => {
    const {setTrail} = useBreadcrumbsContext();

    // Compare by value. A caller writing the array inline would otherwise set
    // state on every render and loop.
    const serialized = crumbs ? JSON.stringify(crumbs) : null;
    const latest = useRef(crumbs);
    latest.current = crumbs;

    const clear = useCallback(() => setTrail(null), [setTrail]);

    useEffect(() => {
        setTrail(latest.current);
        return clear;
    }, [serialized, setTrail, clear]);
};
